import {
  useMutation,
  useQuery,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import { AppError, UserBlockingError } from '@hookmyapp/observability/errors';
import { useFetch } from '@/hooks/use-fetch';
import { logger } from '@/lib/frontend-logger';
import { parseBody } from '@/lib/query/parse-body';
import { throwForResponse } from '@/lib/query/throw-for-response';

export type Transport = (path: string, init?: RequestInit) => Promise<Response>;

export type ApiQueryOptions<T> = Omit<
  UseQueryOptions<T, AppError, T, readonly unknown[]>,
  'queryKey' | 'queryFn'
>;

export type ToRequest<TVars> = (vars: TVars) => {
  path: string;
  init?: RequestInit;
  fallback?: string;
};

export type ApiMutationOptions<TData, TVars> = UseMutationOptions<
  TData,
  AppError,
  TVars
>;

async function runRequest<T>(
  transport: Transport,
  path: string,
  init?: RequestInit,
  fallback?: string,
): Promise<T> {
  let res: Response;
  try {
    res = await transport(path, init);
  } catch (err) {
    if (err instanceof AppError) throw err;

    const reason = err instanceof Error ? err.message : String(err);
    logger.error('Network/transport failure', {
      code: 'QUERY_TRANSPORT_FAIL',
      path,
      reason,
    });
    throw new UserBlockingError({
      message: `Network/transport failure for ${path}`,
      code: 'QUERY_TRANSPORT_FAIL',
      details: { path, reason },
      userMessage: fallback,
    });
  }

  if (!res.ok) await throwForResponse(res, path, fallback);
  return parseBody<T>(res);
}

// Provider-safe core: callers inject the transport, so this hook does not
// require WorkspaceProvider and can be used by context adapters and tests.
export function useApiQueryWithTransport<T>(
  queryKey: readonly unknown[],
  path: string,
  transport: Transport,
  options?: ApiQueryOptions<T>,
): UseQueryResult<T, AppError> {
  return useQuery<T, AppError, T, readonly unknown[]>({
    queryKey,
    queryFn: () => runRequest<T>(transport, path),
    ...options,
  });
}

export function useApiMutationWithTransport<TData, TVars = void>(
  transport: Transport,
  toRequest: ToRequest<TVars>,
  options?: ApiMutationOptions<TData, TVars>,
): UseMutationResult<TData, AppError, TVars> {
  return useMutation<TData, AppError, TVars>({
    mutationFn: (vars) => {
      const { path, init, fallback } = toRequest(vars);
      return runRequest<TData>(transport, path, init, fallback);
    },
    ...options,
  });
}

export function useApiQuery<T>(
  queryKey: readonly unknown[],
  path: string,
  options?: ApiQueryOptions<T>,
): UseQueryResult<T, AppError> {
  const transport = useFetch();
  return useApiQueryWithTransport<T>(queryKey, path, transport, options);
}

export function useApiMutation<TData, TVars = void>(
  toRequest: ToRequest<TVars>,
  options?: ApiMutationOptions<TData, TVars>,
): UseMutationResult<TData, AppError, TVars> {
  const transport = useFetch();
  return useApiMutationWithTransport<TData, TVars>(transport, toRequest, options);
}
